import { useNavigate } from "react-router-dom";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdmin } from "@/context/AdminContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import { SHARED_ARTISTS_KEY, DEFAULT_ARTISTS, ArtistEntry } from "@/utils/artistsStore";
import { useEffect } from "react";

interface ArtistsPageMeta {
  pageVisible: boolean;
  heading: string;
  subheading: string;
  intro: string;
  comingSoonTitle: string;
  comingSoonText: string;
}

const DEFAULT_META: ArtistsPageMeta = {
  pageVisible: true,
  heading: "Meet Our Artists",
  subheading: "The Makers Behind the Magic",
  intro: "Every item in our shop is handcrafted by an independent local artist. Browse our community of makers and discover the stories behind the work.",
  comingSoonTitle: "Coming Soon!",
  comingSoonText: "We're curating our artist directory. Check back soon to meet the talented makers in our shop!",
};

export default function ArtistsPage() {
  const { isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  // Page meta (visibility, headings, etc.)
  const [meta, setMeta] = useAdminStorage<ArtistsPageMeta>("ma_artists_meta", DEFAULT_META);

  // REMOVE redirect — visitors should see the Coming Soon page, not get sent home
  useEffect(() => {
    // No redirect: the coming soon page below is the intended visitor experience
    // when pageVisible is false. Admins can toggle visibility to show full content.
  }, [meta.pageVisible, isAdmin, navigate]);

  // Artists — shared with the homepage Meet Our Makers section
  const [artists, setArtists] = useAdminStorage<ArtistEntry[]>(SHARED_ARTISTS_KEY, DEFAULT_ARTISTS);

  const setM = <K extends keyof ArtistsPageMeta>(key: K, val: ArtistsPageMeta[K]) => {
    setMeta({ ...meta, [key]: val });
  };

  const updateArtist = (id: string, field: keyof ArtistEntry, val: string | boolean) => {
    setArtists(artists.map((a) => a.id === id ? { ...a, [field]: val } : a));
  };

  const addArtist = () => {
    const newArtist: ArtistEntry = {
      id: `artist-${Date.now()}`,
      name: "New Artist",
      medium: "Medium / Category",
      bio: "Click to edit this artist's bio.",
      website: "",
      image: "",
      hasWebsite: false,
      hasImage: false,
      objectPosition: "50% 50%",
    };
    setArtists([...artists, newArtist]);
  };

  const moveArtist = (id: string, dir: -1 | 1) => {
    const idx = artists.findIndex((a) => a.id === id);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= artists.length) return;
    const next = [...artists];
    [next[idx], next[target]] = [next[target], next[idx]];
    setArtists(next);
  };

  const removeArtist = (id: string) => {
    setArtists(artists.filter((a) => a.id !== id));
  };

  const bg = isDark ? "bg-gray-950" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";

  // Coming soon page
  if (!meta.pageVisible) {
    return (
      <>
        <SEOHead
          title="Meet Our Artists — Makers Alley Co"
          description="Discover the talented independent artists behind Makers Alley Co. Handcrafted ceramics, jewelry, paintings, textiles, candles, and more — all made by local creators in the Rogue Valley."
          keywords="Makers Alley artists, handmade artists Medford, local Oregon artists, indie makers, artisan community, ceramic artists, jewelry makers"
          canonicalPath="/artists"
          ogType="website"
          ogTitle="Meet Our Artists — Makers Alley Co"
          ogDescription="Discover the talented independent artists behind Makers Alley Co. Handcrafted ceramics, jewelry, paintings, textiles, candles, and more."
          lastModified="2026-05-10"
          author="Makers Alley Co"
        />
        <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
          <PreviewModeBar />
          <Navbar />
          <div className="flex-1 flex items-center justify-center px-6 py-32 mt-14">
            <div className="text-center max-w-md">
              <div className={`w-24 h-24 flex items-center justify-center rounded-full mx-auto mb-6 ${isDark ? "bg-gray-800" : ""}`} style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}>
                <i className="ri-palette-line text-pink-500 text-4xl"></i>
              </div>
              {isAdmin ? (
                <AdminEditField value={meta.comingSoonTitle} onSave={(v) => setM("comingSoonTitle", v)} className="mb-3" inputClassName="text-3xl font-extrabold text-center">
                  <h1 className={`text-3xl font-extrabold mb-3 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>{meta.comingSoonTitle}</h1>
                </AdminEditField>
              ) : (
                <h1 className={`text-3xl font-extrabold mb-3 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>{meta.comingSoonTitle}</h1>
              )}
              {isAdmin ? (
                <AdminEditField value={meta.comingSoonText} onSave={(v) => setM("comingSoonText", v)} multiline className="mb-8" inputClassName="text-lg text-center">
                  <p className={`text-lg mb-8 ${subColor}`}>{meta.comingSoonText}</p>
                </AdminEditField>
              ) : (
                <p className={`text-lg mb-8 ${subColor}`}>{meta.comingSoonText}</p>
              )}
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </button>
            </div>
          </div>
          {isAdmin && (
            <VisibilityToggle visible={meta.pageVisible} onToggle={() => setM("pageVisible", !meta.pageVisible)} />
          )}
          <Footer />
          <AdminSaveBar />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Meet Our Artists — Makers Alley Co"
        description="Discover the talented independent artists behind Makers Alley Co. Handcrafted ceramics, jewelry, paintings, textiles, candles, and more — all made by local creators in the Rogue Valley."
        keywords="Makers Alley artists, handmade artists Medford, local Oregon artists, indie makers, artisan community, ceramic artists, jewelry makers"
        canonicalPath="/artists"
        ogType="website"
        ogTitle="Meet Our Artists — Makers Alley Co"
        ogDescription="Discover the talented independent artists behind Makers Alley Co. Handcrafted ceramics, jewelry, paintings, textiles, candles, and more."
        lastModified="2026-05-10"
        author="Makers Alley Co"
      />
      <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
        <PreviewModeBar />
        <Navbar />

        {isAdmin && !meta.pageVisible && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-center mt-14">
            <p className="text-yellow-700 text-sm font-semibold">
              <i className="ri-eye-off-line mr-1"></i>
              This page is currently hidden from the public (showing "Coming Soon"). Use the toggle at the bottom to make it visible.
            </p>
          </div>
        )}

        {/* Hero */}
        <div
          className={`pt-32 pb-16 px-6 text-center ${isDark ? "bg-gray-800" : ""}`}
          style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
        >
          <div className="max-w-3xl mx-auto">
            {isAdmin ? (
              <AdminEditField value={meta.subheading} onSave={(v) => setM("subheading", v)} className="mb-2" inputClassName="text-green-600 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{meta.subheading}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{meta.subheading}</span>
            )}
            {isAdmin ? (
              <AdminEditField value={meta.heading} onSave={(v) => setM("heading", v)} className="mt-3 mb-4" inputClassName="text-5xl font-extrabold text-center">
                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{meta.heading}</h1>
              </AdminEditField>
            ) : (
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{meta.heading}</h1>
            )}
            {isAdmin ? (
              <AdminEditField value={meta.intro} onSave={(v) => setM("intro", v)} multiline inputClassName="text-lg text-center">
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">{meta.intro}</p>
              </AdminEditField>
            ) : (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{meta.intro}</p>
            )}
          </div>
        </div>

        {/* Artist grid */}
        <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-16">
          {isAdmin && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 font-semibold flex items-center gap-2">
              <i className="ri-links-line text-amber-500"></i>
              The first 6 artists here are the same ones shown on the homepage. Edits here update the homepage too.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {artists.map((artist) => (
              <ArtistCard
                key={artist.id}
                artist={artist}
                isAdmin={isAdmin}
                isDark={isDark}
                headingColor={headingColor}
                subColor={subColor}
                cardBg={cardBg}
                isFirst={artists.indexOf(artist) === 0}
                isLast={artists.indexOf(artist) === artists.length - 1}
                onUpdate={(field, val) => updateArtist(artist.id, field, val)}
                onRemove={() => removeArtist(artist.id)}
                onMoveUp={() => moveArtist(artist.id, -1)}
                onMoveDown={() => moveArtist(artist.id, 1)}
              />
            ))}

            {isAdmin && (
              <button
                onClick={addArtist}
                className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 p-8 cursor-pointer transition-all hover:border-pink-400 hover:bg-pink-50/50 ${
                  isDark ? "border-gray-600 text-gray-500 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:text-pink-500"
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-100">
                  <i className="ri-add-line text-pink-500 text-2xl"></i>
                </div>
                <span className="text-sm font-semibold">Add Artist</span>
                <span className="text-xs text-center opacity-70">Image &amp; website are optional</span>
              </button>
            )}
          </div>
        </main>

        {isAdmin && (
          <VisibilityToggle visible={meta.pageVisible} onToggle={() => setM("pageVisible", !meta.pageVisible)} />
        )}

        <Footer />
        <AdminSaveBar />
      </div>
    </>
  );
}

// ─── ArtistCard ───────────────────────────────────────────────────────────────

interface ArtistCardProps {
  artist: ArtistEntry;
  isAdmin: boolean;
  isDark: boolean;
  headingColor: string;
  subColor: string;
  cardBg: string;
  isFirst: boolean;
  isLast: boolean;
  onUpdate: (field: keyof ArtistEntry, val: string | boolean) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function ArtistCard({ artist, isAdmin, isDark, headingColor, subColor, cardBg, isFirst, isLast, onUpdate, onRemove, onMoveUp, onMoveDown }: ArtistCardProps) {
  return (
    <div className={`rounded-2xl border overflow-hidden transition-all hover:-translate-y-1 ${cardBg}`}>
      {/* Image */}
      {artist.hasImage && (
        <div className="w-full h-52 overflow-hidden relative">
          {artist.image ? (
            isAdmin ? (
              <AdminImageUpload
                src={artist.image}
                alt={artist.name}
                onReplace={(url) => onUpdate("image", url)}
                objectPosition={artist.objectPosition ?? "50% 50%"}
                onPositionChange={(pos) => onUpdate("objectPosition", pos)}
                mobileObjectPosition={artist.mobileObjectPosition ?? artist.objectPosition ?? "50% 50%"}
                onMobilePositionChange={(pos) => onUpdate("mobileObjectPosition", pos)}

                className="w-full h-full"
                imgClassName="w-full h-full object-cover object-top"
                isDark={isDark}
              />
            ) : (
              <img
                src={artist.image}
                alt={artist.name}
                className="w-full h-full object-cover"
                style={{ objectPosition: artist.objectPosition ?? "50% 50%" }}
              />
            )
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
              <i className="ri-image-line text-4xl text-pink-300"></i>
            </div>
          )}
          {isAdmin && (
            <div className="absolute top-2 right-2 flex gap-1 z-10">
              <button
                onClick={() => onUpdate("hasImage", false)}
                className="text-xs font-bold px-2 py-1 rounded-full cursor-pointer whitespace-nowrap bg-red-500 text-white hover:bg-red-600"
              >
                Remove Image
              </button>
            </div>
          )}
        </div>
      )}

      {!artist.hasImage && isAdmin && (
        <div className="px-5 pt-4 pb-0">
          <button
            onClick={() => onUpdate("hasImage", true)}
            className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer border border-dashed border-pink-200 rounded-lg px-3 py-2 w-full justify-center"
          >
            <i className="ri-image-add-line"></i> Add Image
          </button>
        </div>
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          {isAdmin ? (
            <AdminEditField value={artist.name} onSave={(v) => onUpdate("name", v)} inputClassName="font-bold text-lg">
              <h3 className={`font-bold text-lg ${headingColor}`}>{artist.name}</h3>
            </AdminEditField>
          ) : (
            <h3 className={`font-bold text-lg ${headingColor}`}>{artist.name}</h3>
          )}
          {isAdmin && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={onMoveUp}
                disabled={isFirst}
                title="Move artist up"
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-up-line text-sm"></i>
              </button>
              <button
                onClick={onMoveDown}
                disabled={isLast}
                title="Move artist down"
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-down-line text-sm"></i>
              </button>
              <button
                onClick={onRemove}
                className="w-7 h-7 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 transition-colors"
                title="Remove artist"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          )}
        </div>

        {isAdmin ? (
          <AdminEditField value={artist.medium} onSave={(v) => onUpdate("medium", v)} className="mb-2" inputClassName="text-xs font-bold">
            <span className="inline-block bg-pink-100 text-pink-600 text-xs font-bold px-2.5 py-1 rounded-full mb-2">{artist.medium}</span>
          </AdminEditField>
        ) : (
          <span className="inline-block bg-pink-100 text-pink-600 text-xs font-bold px-2.5 py-1 rounded-full mb-2">{artist.medium}</span>
        )}

        {isAdmin ? (
          <AdminEditField value={artist.bio} onSave={(v) => onUpdate("bio", v)} multiline richText className="mb-3" inputClassName="text-sm leading-relaxed">
            <p className={`text-sm leading-relaxed mb-3 ${subColor}`}>{artist.bio}</p>
          </AdminEditField>
        ) : (
          <p className={`text-sm leading-relaxed mb-3 ${subColor}`}>{artist.bio}</p>
        )}

        {artist.hasWebsite && artist.website ? (
          isAdmin ? (
            <div className="flex items-center gap-2">
              <AdminEditField value={artist.website} onSave={(v) => onUpdate("website", v)} inputClassName="text-sm text-pink-500">
                <a
                  href={artist.website}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-pink-500 hover:text-pink-700 font-semibold transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Visit Website
                </a>
              </AdminEditField>
              <button
                onClick={() => onUpdate("hasWebsite", false)}
                className="text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap"
                title="Remove website link"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ) : (
            <a
              href={artist.website}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-pink-500 hover:text-pink-700 font-semibold transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              Visit Website
            </a>
          )
        ) : isAdmin ? (
          <button
            onClick={() => onUpdate("hasWebsite", true)}
            className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-add-line"></i> Add Website Link
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── VisibilityToggle ─────────────────────────────────────────────────────────

function VisibilityToggle({ visible, onToggle }: { visible: boolean; onToggle: () => void }) {
  return (
    <div className="w-full border-t border-gray-700 bg-gray-900 text-gray-300 px-6 py-5">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-white font-semibold text-sm flex items-center gap-2">
            <i className="ri-shield-check-line text-green-400"></i>
            Admin — Page Visibility
          </p>
          <p className="text-gray-500 text-xs mt-0.5">Toggle whether this page is live or shows "Coming Soon" to visitors</p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-gray-400 text-sm">Page visible to public:</span>
          <button
            type="button"
            onClick={onToggle}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${visible ? "bg-green-500" : "bg-gray-600"}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${visible ? "translate-x-7" : "translate-x-1"}`}></span>
          </button>
          <span className={`font-bold text-sm ${visible ? "text-green-400" : "text-gray-500"}`}>
            {visible ? "Live" : "Coming Soon"}
          </span>
        </label>
      </div>
    </div>
  );
}
